import { useState } from "react";
import Swal from "sweetalert2";
import AdLayout from "../../components/AdLayout";
import CustomerAsyncSelect from "../../components/warehouse/CustomerAsyncSelect";
import LastScanPanel from "../../components/warehouse/LastScanPanel";
import ScannedCodesPanel from "../../components/warehouse/ScannedCodesPanel";
import ScannerInput from "../../components/warehouse/ScannerInput";
import ScanResultTable from "../../components/warehouse/ScanResultTable";
import ScanStats from "../../components/warehouse/ScanStats";
import { buildStats } from "../../components/warehouse/scanResults";
import useWarehouseScan from "../../components/warehouse/useWarehouseScan";
import "../../components/warehouse/warehouse.css";

/**
 * KHO VẬN → Quét xuất kho VN (BRD §5). Bắt buộc chọn khách hàng trước khi quét (KV-006).
 * Quét/paste chỉ kiểm tra; bấm Xác nhận mới chuyển Đang giao.
 */
const ScanOutPage = () => {
  const scan = useWarehouseScan("OUT");
  const [starting, setStarting] = useState(false);

  const session = scan.session;
  const sessionCustomer = session
    ? [session.customerCode, session.customerName].filter(Boolean).join(" - ")
    : "";

  const handleSelectCustomer = async (option) => {
    if (!option) return;
    setStarting(true);
    await scan.openSession(option.value);
    setStarting(false);
  };

  const handleConfirm = async () => {
    const count = scan.pendingLogs.length;
    const confirm = await Swal.fire({
      title: `Xác nhận xuất kho ${count} đơn?`,
      text: `Các đơn hợp lệ của ${sessionCustomer} sẽ chuyển sang trạng thái Đang giao.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Xác nhận xuất kho",
      cancelButtonText: "Hủy",
    });
    if (confirm.isConfirmed) await scan.confirmAll();
  };

  const endSession = async () => {
    const pendingCount = scan.pendingLogs.length;
    const confirm = await Swal.fire({
      title: "Đổi khách hàng / Kết thúc phiên?",
      text:
        pendingCount > 0
          ? `Còn ${pendingCount} mã CHƯA xác nhận — các mã này sẽ bị bỏ, đơn không đổi trạng thái.`
          : `Kết thúc phiên xuất cho ${sessionCustomer}. Sau đó chọn lại khách hàng để quét tiếp.`,
      icon: pendingCount > 0 ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Kết thúc phiên",
      cancelButtonText: "Hủy",
    });
    if (confirm.isConfirmed) await scan.closeSession();
  };

  const last = scan.lastResult;

  return (
    <AdLayout>
      <div className="warehouse-page">
        <div className="warehouse-page__header">
          <h1 className="warehouse-page__title">Quét xuất kho VN</h1>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={endSession} disabled={!session}>
              Đổi khách / Kết thúc phiên
            </button>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-lg-7">
            {!session ? (
              <div className="card mb-3">
                <div className="card-body">
                  <label className="form-label fw-bold">Chọn khách hàng cần xuất hàng</label>
                  <CustomerAsyncSelect
                    value={null}
                    onChange={handleSelectCustomer}
                    autoFocus={!scan.loading}
                    isDisabled={scan.loading || starting}
                  />
                  <div className="form-text">Phải chọn khách hàng trước khi hệ thống nhận mã từ máy quét.</div>
                </div>
              </div>
            ) : (
              <div className="warehouse-customer-banner mb-3">
                <div className="warehouse-customer-banner__label">Đang xuất hàng cho khách</div>
                <div className="warehouse-customer-banner__name">{sessionCustomer}</div>
              </div>
            )}
            <ScannerInput
              enabled={!!session}
              onScan={scan.enqueue}
              disabledPlaceholder="Chọn khách hàng trước khi quét"
            />
            <div className="mt-3">
              <ScanStats items={buildStats("OUT", scan.counts, scan.total, scan.confirmedCount)} />
            </div>
            <div className="mt-3">
              <LastScanPanel result={last} pending={scan.pending} sessionCustomer={sessionCustomer} />
            </div>
          </div>
          <div className="col-lg-5">
            <ScannedCodesPanel
              pendingLogs={scan.pendingLogs}
              confirmedCodes={scan.confirmedCodes}
              confirmLabel="Xác nhận xuất kho"
              onConfirm={handleConfirm}
              onRemove={scan.removePending}
              confirmDisabled={!session || scan.pending > 0}
              confirming={scan.confirming}
            />
          </div>
        </div>

        <h2 className="h6 fw-bold">Kết quả quét gần nhất</h2>
        <ScanResultTable rows={scan.recent} />
      </div>
    </AdLayout>
  );
};

export default ScanOutPage;
